// Localizes next-best-action types and reasons through the
// `crm.features.deals.nextBestAction.*` catalog.
//
// The engine emits machine codes and params only — never UI text — so the card
// can render in either locale without the server knowing the language. A code
// without a catalog entry (an action type added before its copy) falls back to
// the code itself instead of rendering a raw catalog key.

import type { I18n } from '@govard/i18n-core';

import type { NbaActionType, NbaReasonCode } from '../../sales-health/nba-engine.js';

/** Full catalog prefix as resolved by the merged `crm` namespace. */
export const NBA_LABEL_KEY_PREFIX = 'crm.features.deals.nextBestAction.';

export const NBA_CONFIDENCE_LABEL_KEY = `${NBA_LABEL_KEY_PREFIX}confidence.label`;
export const NBA_IMPACT_LABEL_KEY = `${NBA_LABEL_KEY_PREFIX}impact.label`;

/** Translators the label helpers need — matches the useTranslation() result. */
export type NbaLabelI18n = Pick<I18n, 't' | 'tc'>;

/** Catalog key of one action title. */
export function nbaActionTitleKey(type: NbaActionType): string {
  return `${NBA_LABEL_KEY_PREFIX}title.${type}`;
}

/** Catalog key of one reason ("why this action"). */
export function nbaReasonKey(code: NbaReasonCode): string {
  return `${NBA_LABEL_KEY_PREFIX}reason.${code}`;
}

/** Localized action title; the raw type is the fallback for an unknown code. */
export function nbaActionTitleText(i18n: NbaLabelI18n, type: NbaActionType): string {
  const key = nbaActionTitleKey(type);
  const translated = i18n.t(key);
  return translated === key ? type : translated;
}

/**
 * Localized reason sentence with the engine params (days, count, gaps, stage).
 * A missing or ill-typed param would render a half-empty sentence, so an
 * unresolved key falls back to the reason code for the same reason as above.
 */
export function nbaReasonText(
  i18n: NbaLabelI18n,
  code: NbaReasonCode,
  params: Record<string, string | number> = {},
): string {
  const key = nbaReasonKey(code);
  const translated = i18n.t(key, params);
  return translated === key ? code : translated;
}

/**
 * Catalog key of one attention-queue reason chip. The queue contract carries
 * the codes without params, so the chips are short labels rather than the
 * `reason.*` sentences above.
 */
export function nbaQueueReasonKey(code: NbaReasonCode): string {
  return `${NBA_LABEL_KEY_PREFIX}queue.reason.${code}`;
}

/** Localized queue chip; an unknown code falls back to the code itself. */
export function nbaQueueReasonText(i18n: NbaLabelI18n, code: NbaReasonCode): string {
  const key = nbaQueueReasonKey(code);
  const translated = i18n.t(key);
  return translated === key ? code : translated;
}

/** Signed projected impact, e.g. "+22 п.п." / "+22 pp". */
export function nbaImpactText(i18n: NbaLabelI18n, projectedImpact: number): string {
  return i18n.tc(`${NBA_LABEL_KEY_PREFIX}impact.points`, projectedImpact);
}
